import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  useMediaQuery
} from '@mui/material';
import {
  AutoAwesome as AgentsIcon,
  People as UsersIcon,
  TrendingUp as TrendingIcon,
  Assessment as ReportsIcon,
  History as HistoryIcon,
  AddCard as AddCardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import KpiCard from './KpiCard';
import { adminStatsApi, AdminStats } from '../../utils/adminStatsApi';
import { useAuth } from '../../contexts/AuthContext';
import { creditApi, OrganizationCredits } from '../../utils/creditApi';
import AddCreditsModal from '../AddCreditsModal';
import { useTranslation } from 'react-i18next';

const AdminOverview: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationCredits, setOrganizationCredits] = useState<OrganizationCredits | null>(null);
  const [addCreditsModalOpen, setAddCreditsModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Carregar estatísticas e créditos em paralelo
        const [statsData, creditsData] = await Promise.all([
          adminStatsApi.getOverviewStats(),
          creditApi.getOrganizationCredits(),
        ]);

        setStats(statsData);
        setOrganizationCredits(creditsData);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setError(t('admin.adminOverview.errorLoadingData'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Verificar se os créditos atingiram o limite e se não é WhiteLabel gerenciado
  const shouldShowAddCreditsButton = () => {
    if (!organizationCredits) return false;

    // Verificar se a organização não é gerenciada por WhiteLabel
    if (currentOrganization?.isWhiteLabelManaged === true) return false;

    // Verificar se os créditos estão no limite (90% ou mais usados)
    const usagePercentage = (organizationCredits.usedThisMonth / organizationCredits.totalAvailableCredits) * 100;
    return usagePercentage >= 90;
  };

  const handleAddCreditsSuccess = async () => {
    // Recarregar os créditos após a compra
    try {
      const creditsData = await creditApi.getOrganizationCredits();
      setOrganizationCredits(creditsData);
    } catch (err) {
      console.error('Erro ao recarregar créditos:', err);
    }
  };

  const quickActions = [
    {
      title: t('admin.adminOverview.agents'),
      description: t('admin.adminOverview.agentsDescription'),
      icon: <AgentsIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.mode === 'dark' ? '#65f0c5' : '#00f5ff',
      action: () => navigate('/admin/agents')
    },
    {
      title: t('admin.adminOverview.systemUsers'),
      description: t('admin.adminOverview.usersDescription'),
      icon: <UsersIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.mode === 'dark' ? '#4ade80' : '#388e3c',
      action: () => navigate('/admin/users')
    },
    {
      title: t('admin.adminOverview.reports'),
      description: t('admin.adminOverview.reportsDescription'),
      icon: <ReportsIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.mode === 'dark' ? '#fbbf24' : '#f57c00',
      action: () => navigate('/admin/reports')
    },
    {
      title: t('admin.adminOverview.performance'),
      description: t('admin.adminOverview.performanceDescription'),
      icon: <TrendingIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.mode === 'dark' ? '#a78bfa' : '#7b1fa2',
      action: () => alert(t('admin.adminOverview.featureInDevelopment'))
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('admin.adminOverview.title')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {t('admin.adminOverview.title')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/admin/credit-history')}
          >
            {t('sidebar.history')}
          </Button>

          {shouldShowAddCreditsButton() && (
            <Button
              variant="contained"
              color="primary"
              sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
              startIcon={<AddCardIcon />}
              onClick={() => setAddCreditsModalOpen(true)}
            >
              {isMobile ? t('sidebar.credits') : t('sidebar.addCredits')}
            </Button>
          )}
        </Box>
      </Box>

      {/* KPI Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3
        }}
      >
        <KpiCard
          title={t('admin.adminOverview.agents')}
          value={stats?.activeAgents.toString() || "0"}
          subtitle={t('admin.adminOverview.active')}
          icon={<AgentsIcon />}
          color={theme.palette.mode === 'dark' ? '#475569' : '#1a1a1a'}
        />
        <KpiCard
          title={t('admin.adminOverview.conversations')}
          value={stats?.conversationsToday.toString() || "0"}
          subtitle={t('admin.adminOverview.today')}
          icon={<TrendingIcon />}
          color={theme.palette.mode === 'dark' ? '#65f0c5' : '#3498db'}
        />
        <KpiCard
          title={t('admin.adminOverview.leads')}
          value={stats?.leadsToday.toString() || "0"}
          subtitle={t('admin.adminOverview.generatedToday')}
          icon={<ReportsIcon />}
          color={theme.palette.mode === 'dark' ? '#4ade80' : '#35e87c'}
        />
        <KpiCard
          title={t('admin.adminOverview.users')}
          value={stats?.activeUsers.toString() || "0"}
          subtitle={t('admin.adminOverview.inSystem')}
          icon={<UsersIcon />}
          color={theme.palette.mode === 'dark' ? '#fbbf24' : '#f06e65'}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3
        }}
      >
        {quickActions.map((action, index) => (
          <Card
            key={index}
            sx={{
              height: '100%',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: action.color + '20',
                  color: action.color,
                  mb: 2
                }}
              >
                {action.icon}
              </Box>
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {action.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {action.description}
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button
                variant="contained"
                onClick={action.action}
                sx={{
                  bgcolor: action.color,
                  color: '#000',
                  '&:hover': {
                    bgcolor: action.color + 'dd'
                  }
                }}
              >
                {t('admin.adminOverview.access')}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr'
          },
          gap: 3
        }}
      >
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('admin.adminOverview.quickStatistics')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 3
            }}
          >
            <Box textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {stats?.activeAgents || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.adminOverview.activeAgents')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('admin.adminOverview.ofTotal', { count: stats?.totalAgents || 0 })}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {stats?.conversationsToday || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.adminOverview.conversationsToday')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('admin.adminOverview.ofTotal', { count: stats?.totalConversations || 0 })}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {stats?.leadsToday || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.adminOverview.leadsGeneratedToday')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('admin.adminOverview.ofTotal', { count: stats?.totalLeads || 0 })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('admin.adminOverview.quickActions')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AgentsIcon />}
              onClick={() => navigate('/admin/agents')}
            >
              {t('admin.adminOverview.createNewAgent')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ReportsIcon />}
              onClick={() => navigate('/admin/reports')}
            >
              {t('admin.adminOverview.viewReports')}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Modal de Adicionar Créditos */}
      <AddCreditsModal
        open={addCreditsModalOpen}
        onClose={() => setAddCreditsModalOpen(false)}
        onSuccess={handleAddCreditsSuccess}
      />
    </Box>
  );
};

export default AdminOverview;